import addDays from 'date-fns/addDays'
import first from 'lodash/first'
import groupBy from 'lodash/groupBy'
import last from 'lodash/last'
import { action, comparer, computed, makeObservable, override } from 'mobx'
import { getValue, isPending } from 'mobx-async'

import { Alert } from '@avantstay/backoffice-core'
import { SortDirection } from '@avantstay/data-grid'

import { BaseStore } from '@new/__global/components/FastTapeChart/__types'
import {
  getListingUnassignedId,
  getSortedListByCheckInDate,
  parseDate,
  resolveWithAnimationFrame,
  shortISODate,
} from '@new/__global/components/FastTapeChart/__utils'
import { TapeChartFiltersApiStore } from '@new/__global/components/FastTapeChart/external/TapeChartFiltersApiStore'
import { sortOrderMappings } from '@new/__global/components/FiltersFramework/_utils'

import { trackError } from '@tracking/customEventReport'

import { ApiStoreObservables } from './ApiStoreObservables'
import { arriere } from './arriere'

export class ApiStore extends ApiStoreObservables {
  constructor(
    private api: typeof arriere,
    private getStateStore: () => BaseStore['stateStore'],
    private getNavigationStore: () => BaseStore['navigationStore'],
    private getFilterApiStore: () => Partial<TapeChartFiltersApiStore>,
  ) {
    super()

    makeObservable<
      ApiStore,
      | 'visibleDates'
      | '__ASYNC_listings_results'
      | '__ASYNC_unfiltered_listings_results'
      | '__ASYNC_bookings'
      | '__ASYNC_unassigned_bookings'
      | '__ASYNC_tapeChartData'
      | '__ASYNC_blocks'
      | '__ASYNC_listings_extended_results'
      | '__ASYNC_rates'
      | '__ASYNC_blocksByListing'
      | '__ASYNC_bookingsByListing'
      | '__ASYNC_unassignedBookingsByListing'
      | 'unfilteredListingsResults'
      | 'listingsIds'
      | 'blocks'
      | 'bookingsByHome'
      | 'unassignedBookingsByHome'
      | 'rates'
    >(this, {
      visibleDates: computed,
      __ASYNC_listings_results: computed,
      __ASYNC_unfiltered_listings_results: computed,
      __ASYNC_bookings: computed,
      __ASYNC_unassigned_bookings: computed,
      __ASYNC_tapeChartData: computed,
      __ASYNC_blocks: computed,
      __ASYNC_listings_extended_results: computed,
      __ASYNC_rates: computed,
      __ASYNC_blocksByListing: computed,
      __ASYNC_bookingsByListing: computed,
      __ASYNC_unassignedBookingsByListing: computed,
      rawListingsResults: computed,
      unfilteredListingsResults: computed,
      listingsIds: computed({ equals: comparer.structural }),
      totalListings: computed,
      listingsExtendedResults: computed,
      blocks: computed,
      allBlocks: computed,
      blocksByHome: computed,
      bookings: computed,
      unassignedBookings: computed,
      bookingsByHome: computed,
      unassignedBookingsByHome: computed,
      allBookingsByHome: computed,
      allUnassignedBookingsByHome: computed,
      rates: computed,
      allRates: computed,
      calculateUnfilteredSortedListingsList: computed,
      unfilteredSortedListingsList: computed,
      allListingsToRender: computed,
      isLoadingAnimationFrame: computed,
      isLoadingBookings: computed,
      isLoadingUnassignedBookings: computed,
      isLoadingBlocks: computed,
      isLoadingExtendedInfo: computed,
      isLoading: computed,
      reloadBookings: action.bound,
      reloadBlockedPeriods: override,
    })
  }

  //--- Core Store Accessors ---
  get stateStore() {
    return this.getStateStore()
  }

  get navigationStore() {
    return this.getNavigationStore()
  }

  get tapeChartFilterStore() {
    return this.getFilterApiStore()
  }

  //--- Computed Filters & Parameters ---
  private get visibleDates() {
    return this.stateStore?.visibleDates
  }

  private getSortingParams() {
    const defaultSortOrder = sortOrderMappings[SortDirection.Asc]

    return {
      sortBy: this.navigationStore.sortBy() ?? 'name',
      sortOrder: sortOrderMappings[this.navigationStore.sortOrder()] ?? defaultSortOrder,
    }
  }

  //--- Asynchronous Data Fetching Computed Properties (Private) ---
  private get __ASYNC_listings_results() {
    try {
      return this.api.fetchListings({
        terms: this.navigationStore.searchTerms,
        filters: {
          ...this.tapeChartFilterStore.unitsFilters,
        },
        ...this.getSortingParams(),
      })
    } catch (error) {
      Alert.Error(`Error: ${error}`)
      trackError(`Failed attempt to fetch listings: ${error}`, { error })
      throw error
    }
  }

  private get __ASYNC_unfiltered_listings_results() {
    try {
      return this.api.fetchAllListings({})
    } catch (error) {
      Alert.Error(`Error: ${error}`)
      trackError(`Failed attempt to fetch listings: ${error}`, { error })
      throw error
    }
  }

  private get __ASYNC_bookings() {
    void [this.refreshBookingsCount]
    const startDate = shortISODate(addDays(parseDate(first(this.visibleDates)), -1))
    const endDate = shortISODate(last(this.visibleDates))
    const listingsResults = getValue(this.__ASYNC_listings_results)
    const homeIds = listingsResults?.sortedRawResults?.map(listing => listing.id)

    if (!homeIds?.length || listingsResults === undefined) {
      return Promise.resolve(undefined)
    }

    try {
      return this.api.fetchBookings({
        startDate,
        endDate,
        homeIds,
      })
    } catch (error) {
      Alert.Error(`Error: ${error}`)
      trackError(`Failed attempt to fetch bookings: ${error}`, { error })
      throw error
    }
  }

  private get __ASYNC_unassigned_bookings() {
    void [this.refreshBookingsCount]
    const listingIds = this.listingsIds
    const visibleDates = this.visibleDates

    if (!listingIds?.length || !visibleDates) {
      return Promise.resolve(undefined)
    }

    try {
      const startDate = shortISODate(addDays(parseDate(first(visibleDates)), -1))
      const endDate = shortISODate(last(visibleDates))

      const filteredUnitsId = this.unfilteredListingsResults?.map(unit => unit.id) || listingIds

      return this.api.fetchUnassignedBookings({
        startDate,
        endDate,
        homeIds: filteredUnitsId,
      })
    } catch (error) {
      Alert.Error(`Error: ${error}`)
      trackError(`Failed attempt to fetch unassigned bookings: ${error}`, { error })
      throw error
    }
  }

  private get __ASYNC_tapeChartData() {
    void [this.refreshBlockedPeriodsCount]
    const startDate = shortISODate(addDays(parseDate(first(this.visibleDates)), -1))
    const endDate = shortISODate(addDays(parseDate(last(this.visibleDates)) ?? 0, 1))
    const homeIds = this.listingsIds || []

    if (!homeIds.length) {
      return Promise.resolve(undefined)
    }

    return this.api.fetchTapeChartData({ startDate, endDate, homeIds })
  }

  private get __ASYNC_blocks() {
    return this.__ASYNC_tapeChartData.then(data => data?.blocks)
  }

  private get __ASYNC_listings_extended_results() {
    const listingsIds = this.listingsIds

    if (!listingsIds?.length) {
      return Promise.resolve(undefined)
    }

    try {
      return this.api.fetchHomesListExtendedInfo({
        homeIds: this.listingsIds ?? [],
      })
    } catch (error) {
      Alert.Error(`Error: ${error}`)
      trackError(`Failed attempt to fetch listings data: ${error}`, { error })
      throw error
    }
  }

  private get __ASYNC_rates() {
    return this.__ASYNC_tapeChartData.then(data => data?.rates)
  }

  //--- Derived Asynchronous Data
  private get __ASYNC_blocksByListing() {
    return this.__ASYNC_blocks
      .then(blocks => (blocks ? groupBy(blocks, it => it.homeId) : undefined))
      .then(resolveWithAnimationFrame)
  }

  private get __ASYNC_bookingsByListing() {
    return this.__ASYNC_bookings
      .then(bookings => (bookings ? groupBy(bookings, it => it.homeId) : undefined))
      .then(resolveWithAnimationFrame)
  }

  private get __ASYNC_unassignedBookingsByListing() {
    return this.__ASYNC_unassigned_bookings
      .then(bookings =>
        bookings ? groupBy(bookings, it => getListingUnassignedId(it.homeId)) : undefined,
      )
      .then(resolveWithAnimationFrame)
  }

  //--- Public Getters for Data Access ---
  get rawListingsResults() {
    return getValue(this.__ASYNC_listings_results)
  }

  private get unfilteredListingsResults() {
    return getValue(this.__ASYNC_unfiltered_listings_results)
  }

  private get listingsIds() {
    const listingsResults = getValue(this.__ASYNC_listings_results)
    if (!listingsResults) {
      return undefined
    }
    return listingsResults.sortedRawResults?.map(listing => listing.id)
  }

  get totalListings() {
    return this.rawListingsResults?.total ?? 0
  }

  get listingsExtendedResults() {
    return getValue(this.__ASYNC_listings_extended_results)
  }

  private get blocks() {
    return getValue(this.__ASYNC_blocksByListing)
  }

  get allBlocks() {
    return getValue(this.__ASYNC_blocks)
  }

  get blocksByHome() {
    return this.blocks
  }

  get bookings() {
    return getValue(this.__ASYNC_bookings)
  }

  get unassignedBookings() {
    return getValue(this.__ASYNC_unassigned_bookings)
  }

  private get bookingsByHome() {
    return getValue(this.__ASYNC_bookingsByListing)
  }

  private get unassignedBookingsByHome() {
    return getValue(this.__ASYNC_unassignedBookingsByListing)
  }

  get allBookingsByHome() {
    return this.bookingsByHome
  }

  get allUnassignedBookingsByHome() {
    return this.unassignedBookingsByHome
  }

  private get rates() {
    return getValue(this.__ASYNC_rates)
  }

  get allRates() {
    return this.rates
  }

  //--- Data Processing & Presentation Computeds ---
  get calculateUnfilteredSortedListingsList() {
    const listings = this.unfilteredListingsResults
    const unassignedBookings = this.allUnassignedBookingsByHome

    return resolveWithAnimationFrame(getSortedListByCheckInDate(listings, unassignedBookings))
  }

  get unfilteredSortedListingsList() {
    return getValue(this.calculateUnfilteredSortedListingsList)
  }

  // Should be used only to render list
  get allListingsToRender() {
    return this.rawListingsResults?.sortedRawResults
  }

  //--- Loading State ---

  get isLoadingAnimationFrame() {
    return (
      isPending(this.__ASYNC_blocksByListing) ||
      isPending(this.__ASYNC_bookingsByListing) ||
      isPending(this.__ASYNC_unassignedBookingsByListing)
    )
  }

  get isLoadingBookings() {
    return isPending(this.__ASYNC_bookings)
  }

  get isLoadingUnassignedBookings() {
    return isPending(this.__ASYNC_unassigned_bookings)
  }

  get isLoadingBlocks() {
    return this.isLoadingAnimationFrame || this.forceBlocksLoadingOverlay
  }

  get isLoadingExtendedInfo() {
    return isPending(this.__ASYNC_listings_extended_results)
  }

  get isLoading() {
    return (
      this.forceLoadingOverlay ||
      isPending(this.__ASYNC_bookings) ||
      isPending(this.__ASYNC_listings_results) ||
      isPending(this.__ASYNC_blocks)
    )
  }

  //--- Actions ---
  reloadBlockedPeriods() {
    this.api.fetchTapeChartData.clear()
    super.reloadBlockedPeriods()
  }

  reloadBookings() {
    this.api.fetchBookings.clear()
    this.api.fetchUnassignedBookings.clear()
    this.refreshBookingsCount += 1
  }
}
